import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import api from '../../../services/api';
import { formatDate } from '../../../utils/Utils';

const StatusBadge = styled.span`
  display: inline-block;
  padding: 3px 10px;
  border-radius: 4px;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 12px;
  font-weight: 600;
  background: ${({ $status }) =>
    $status === 'approved' ? '#10b98115' :
    $status === 'rejected' ? '#ef444415' :
    $status === 'canceled' ? '#6b728015' :
    '#f59e0b15'};
  color: ${({ $status }) =>
    $status === 'approved' ? '#10b981' :
    $status === 'rejected' ? '#ef4444' :
    $status === 'canceled' ? '#6b7280' :
    '#f59e0b'};
`;

export const useMyCertificationApi = (queryParams) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const [fetching, setFetching] = useState(true);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ totalRowCount: 0, pageSize: 10, pageIndex: 0 });
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [categories, setCategories] = useState([]);
  const lastValidQueryParams = useRef(null);

  useEffect(() => {
    api.get('/v1/certification-categories?pageSize=500')
      .then((res) => setCategories(res.data?.data?.data || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    const paramsToUse = queryParams && Object.keys(queryParams).length > 0
      ? queryParams
      : lastValidQueryParams.current || { pageNumber: 1, pageSize: 10 };
    lastValidQueryParams.current = paramsToUse;
    setFetching(true);
    api.get(`/v1/my-certifications?${new URLSearchParams(paramsToUse)}`)
      .then((res) => {
        const result = res.data?.data;
        const items = Array.isArray(result) ? result : (result?.data || []);
        setData(items);
        setPagination({
          totalRowCount: result?.totalRowCount || items.length,
          pageIndex: result?.pageIndex ? result.pageIndex - 1 : 0,
          pageSize: result?.pageSize || items.length || 10,
        });
      })
      .finally(() => setFetching(false));
  }, [queryParams, refreshTrigger]);

  const create = useCallback(async (formData) => {
    setFetching(true);
    try {
      await api.post('/v1/my-certifications', formData);
      setRefreshTrigger((p) => p + 1);
      return true;
    } catch {
      setFetching(false);
      return false;
    }
  }, []);

  const update = useCallback(async (id, formData) => {
    setFetching(true);
    try {
      await api.put(`/v1/my-certifications/${id}`, formData);
      setRefreshTrigger((p) => p + 1);
      return true;
    } catch {
      setFetching(false);
      return false;
    }
  }, []);

  const _delete = useCallback(async (ids) => {
    try {
      for (const id of ids) await api.delete(`/v1/my-certifications/${id}`);
      setRefreshTrigger((p) => p + 1);
      return true;
    } catch {
      return false;
    }
  }, []);

  const statusLabels = useMemo(() => ({
    waiting: t('certification.waiting'),
    approved: t('certification.approved'),
    rejected: t('certification.rejected'),
    canceled: t('certification.canceled'),
  }), [t]);

  const columns = useMemo(() => [
    { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
    { name: 'name', label: t('certification.name'), options: { filter: true, sort: true, filterType: 'textField', filterOptions: { defaultOp: 'like' } } },
    { name: 'issuingOrganization', label: t('certification.issuingOrganization'), options: { filter: true, sort: true, filterType: 'textField', filterOptions: { defaultOp: 'like' } } },
    { name: 'issueDate', label: t('certification.issueDate'), options: { filter: false, sort: true, customBodyRender: (v) => formatDate(v, lang) } },
    { name: 'score', label: t('certification.score'), options: { filter: true, sort: false, filterType: 'textField', filterOptions: { defaultOp: 'gte' } } },
    {
      name: 'status', label: t('certification.status'),
      options: {
        filter: true, sort: false,
        filterType: 'dropdown',
        filterOptions: {
          defaultOp: 'eq',
          names: ['waiting', 'approved', 'rejected', 'canceled'],
          renderValue: (v) => statusLabels[v] || v,
        },
        customFilterListOptions: { render: (v) => statusLabels[v] || v },
        customBodyRender: (v) => (
          <StatusBadge $status={v}>{statusLabels[v] || v}</StatusBadge>
        ),
      },
    },
  ], [t, statusLabels, lang]);

  const objectSchema = useMemo(() => [
    {
      name: 'categoryId', label: t('certification.category'), type: 'select', grid: 12,
      options: categories.map((c) => ({ id: c.id, name: c.name })),
      idKey: 'id', labelKey: 'name',
    },
    { name: 'name', label: t('certification.name'), type: 'text', grid: 12, required: true },
    { name: 'subject', label: t('certification.subject'), type: 'text', grid: 12 },
    { name: 'issuingOrganization', label: t('certification.issuingOrganization'), type: 'text', grid: 12 },
    { name: 'issueDate', label: t('certification.issueDate'), type: 'date', grid: 6 },
    { name: 'expirationDate', label: t('certification.expirationDate'), type: 'date', grid: 6 },
    { name: 'credentialId', label: t('certification.credentialId'), type: 'text', grid: 6 },
    { name: 'score', label: t('certification.score'), type: 'text', grid: 6 },
    {
      name: 'documentUrl', label: t('certification.documentUrl'), type: 'pictureUpload', grid: 12,
      accept: 'image/*,.pdf', validTypes: ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'application/pdf'],
      maxSize: 10,
    },
    { name: 'notes', label: t('certification.notes'), type: 'textarea', grid: 12 },
  ], [t, categories]);

  return { objectSchema, columns, data, fetching, pagination, create, update, _delete };
};

export const useCertificationCategoryApi = (queryParams) => {
  const { t } = useTranslation();

  const [fetching, setFetching] = useState(true);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ totalRowCount: 0, pageSize: 10, pageIndex: 0 });
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const lastValidQueryParams = useRef(null);

  useEffect(() => {
    const paramsToUse = queryParams && Object.keys(queryParams).length > 0
      ? queryParams
      : lastValidQueryParams.current || { pageNumber: 1, pageSize: 10 };
    lastValidQueryParams.current = paramsToUse;
    setFetching(true);
    api.get(`/v1/certification-categories?${new URLSearchParams(paramsToUse)}`)
      .then((res) => {
        const result = res.data?.data;
        const items = Array.isArray(result) ? result : (result?.data || []);
        setData(items);
        setPagination({
          totalRowCount: result?.totalRowCount || items.length,
          pageIndex: result?.pageIndex ? result.pageIndex - 1 : 0,
          pageSize: result?.pageSize || items.length || 10,
        });
      })
      .finally(() => setFetching(false));
  }, [queryParams, refreshTrigger]);

  const create = useCallback(async (formData) => {
    setFetching(true);
    try {
      await api.post('/v1/certification-categories', formData);
      setRefreshTrigger((p) => p + 1);
      return true;
    } catch {
      setFetching(false);
      return false;
    }
  }, []);

  const update = useCallback(async (id, formData) => {
    setFetching(true);
    try {
      await api.put(`/v1/certification-categories/${id}`, formData);
      setRefreshTrigger((p) => p + 1);
      return true;
    } catch {
      setFetching(false);
      return false;
    }
  }, []);

  const _delete = useCallback(async (ids) => {
    try {
      if (ids.length === 1) {
        await api.delete(`/v1/certification-categories/${ids[0]}`);
      } else {
        await api.post('/v1/certification-categories/batch-delete', ids);
      }
      setRefreshTrigger((p) => p + 1);
      return true;
    } catch {
      return false;
    }
  }, []);

  const columns = useMemo(() => [
    { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
    { name: 'name', label: t('certificationCategory.name'), options: { filter: true, sort: true, filterType: 'textField', filterOptions: { defaultOp: 'like' } } },
    { name: 'orderIndex', label: t('certificationCategory.orderIndex'), options: { filter: false, sort: true } },
  ], [t]);

  const objectSchema = useMemo(() => [
    { name: 'name', label: t('certificationCategory.name'), type: 'text', grid: 12, required: true },
    { name: 'orderIndex', label: t('certificationCategory.orderIndex'), type: 'number', grid: 12 },
  ], [t]);

  return { objectSchema, columns, data, fetching, pagination, create, update, _delete };
};
